import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaClipboardList,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaSpinner,
  FaSync,
  FaChartBar,
  FaClock,
  FaGraduationCap,
  FaAward,
} from "react-icons/fa";
import AdminCertificates from "./AdminCertificates";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ExamSet = {
  id: number;
  displayName: string;
  setName: string;
  level: string;
  variant: number;
  lesenDuration: number;
  horenDuration: number;
  schreibenDuration: number;
  totalDuration: number;
  questionCount: number;
};

const AdminExamManagement: React.FC = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState<ExamSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<"exams" | "assignments" | "statistics" | "certificates">("exams");

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/exams`, {
        credentials: "include",
      });

      if (response.ok) {
        const result = await response.json();
        setExams(result.data || result);
        setLastUpdated(new Date());
      } else {
        setError("Failed to fetch exam sets");
      }
    } catch (err) {
      console.error("Error fetching exams:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchExams();
    setRefreshing(false);
  };

  const handleDelete = async (examId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this exam set? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/${examId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setExams(exams.filter((exam) => exam.id !== examId));
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete exam set");
      }
    } catch (err) {
      console.error("Error deleting exam:", err);
      alert("Network error. Please try again.");
    }
  };

  // Group exams by level
  const examsByLevel = exams.reduce<Record<string, ExamSet[]>>((acc, exam) => {
    const level = exam.level || "Unspecified";
    if (!acc[level]) acc[level] = [];
    acc[level].push(exam);
    return acc;
  }, {});

  // Sort levels
  const levelOrder = ["A1", "A2", "B1", "B2"];
  const sortedLevels = Object.keys(examsByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  const renderExamTable = (level: string, examList: ExamSet[]) => {
    return (
      <div className="mb-10" key={level}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <FaClipboardList />
            Level: {level}
          </h3>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
            {examList.length} exam set(s)
          </span>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Exam Name
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {examList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-10 text-center text-gray-400 font-semibold"
                    >
                      No exam sets found for this level.
                    </td>
                  </tr>
                ) : (
                  examList.map((exam, index) => (
                    <tr
                      key={exam.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-semibold text-gray-900">
                          {exam.displayName || exam.setName || "Unnamed Exam"}{" "}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-gray-900">
                            {exam.totalDuration} min
                          </span>
                          <span className="text-xs text-gray-500">
                            L:{exam.lesenDuration} H:{exam.horenDuration} S:
                            {exam.schreibenDuration}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-10 h-10 bg-green-100 text-green-800 font-bold rounded-md">
                          {exam.questionCount}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(`/admin/exams/${exam.id}/questions`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-semibold"
                            title="Manage Questions"
                          >
                            <FaClipboardList size={14} />
                            Questions
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/admin/exams/${exam.id}/assign`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            title="Assign to Batches"
                          >
                            <FaUsers size={14} />
                            Assign
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/admin/exams/${exam.id}/edit`)
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                            title="Edit Exam"
                          >
                            <FaEdit size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(exam.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-semibold"
                            title="Delete Exam"
                          >
                            <FaTrash size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaClipboardList />}
        title="Manage Exam Sets"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exam Sets" },
        ]}
      />

      {/* Toolbar */}
      <div className="flex items-center justify-end gap-3 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <span className="text-xs text-gray-500">
          Updated:{" "}
          {lastUpdated.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <button
          onClick={refreshData}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm font-semibold shadow-sm"
        >
          <FaSync className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Tab bar */}
        <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-lg shadow-sm overflow-x-auto">
          {([
            { key: "exams",        label: "Exam Sets",              icon: <FaClipboardList size={14} /> },
            { key: "assignments",  label: "Assignments & Grading",  icon: <FaGraduationCap size={14} /> },
            { key: "statistics",   label: "Statistics",             icon: <FaChartBar size={14} /> },
            { key: "certificates", label: "Teilnahmebestätigung",   icon: <FaAward size={14} /> },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-[#009fe3] text-[#009fe3]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Exam Sets */}
        {activeTab === "exams" && (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                onClick={() => navigate("/admin/exams/create")}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-md font-semibold bg-[#009fe3] hover:bg-cyan-600 text-white shadow-sm transition"
              >
                <FaPlus size={13} />
                Create Exam Set
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
                <p className="font-semibold">Error</p>
                <p>{error}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
                <FaSpinner className="animate-spin" />
                Loading exam sets...
              </div>
            ) : sortedLevels.length === 0 ? (
              <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow-lg">
                <FaClipboardList className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Exam Sets Yet</h3>
                <p className="mb-4">Create your first exam set to get started!</p>
                <button
                  onClick={() => navigate("/admin/exams/create")}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors font-semibold"
                >
                  <FaPlus />
                  Create Exam Set
                </button>
              </div>
            ) : (
              sortedLevels.map((level) => renderExamTable(level, examsByLevel[level]))
            )}
          </>
        )}

        {/* Tab: Assignments */}
        {activeTab === "assignments" && (
          <div className="bg-white rounded-xl shadow p-10 text-center">
            <FaGraduationCap className="mx-auto text-[#009fe3] mb-4" size={48} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Assignments & Grading</h3>
            <p className="text-gray-500 mb-6">View batch assignments, grade student attempts, and publish results.</p>
            <button
              onClick={() => navigate("/admin/exams/assignments")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
            >
              <FaGraduationCap size={14} />
              Open Assignments
            </button>
          </div>
        )}

        {/* Tab: Statistics */}
        {activeTab === "statistics" && (
          <div className="bg-white rounded-xl shadow p-10 text-center">
            <FaChartBar className="mx-auto text-[#009fe3] mb-4" size={48} />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Global Statistics</h3>
            <p className="text-gray-500 mb-6">View exam performance statistics across all batches and levels.</p>
            <button
              onClick={() => navigate("/admin/exams/statistics")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
            >
              <FaChartBar size={14} />
              Open Statistics
            </button>
          </div>
        )}

        {/* Tab: Certificates */}
        {activeTab === "certificates" && <AdminCertificates />}
      </main>
    </div>
  );
};

export default AdminExamManagement;
